/**
 * The seeded PRNG every generator in the server draws from.
 *
 * `Math.random()` is banned in map generation and simulation. Determinism is not
 * a nicety here — it is what makes a balance complaint reproducible ("seed
 * 91422 spawns you in a desert pocket") and what makes the deterministic
 * AI-vs-AI replay tests in the balance phase possible at all. Same seed, same
 * map, forever.
 *
 * mulberry32: 32 bits of state, one multiply-xorshift round, no dependency.
 * Its statistical quality is far beyond what terrain noise and spawn jitter
 * need, and unlike a Mersenne Twister it fits in a cache line and is trivial to
 * re-implement identically if this ever has to run somewhere else.
 */
/**
 * A generator of uniform values in [0, 1).
 *
 * Held as a plain closure rather than a class so it can be passed around as a
 * drop-in replacement for `Math.random` without anyone having to remember to
 * bind a method.
 */
export function createRng(seed) {
    // Coerce through a uint32: NaN, negatives and fractional seeds all become a
    // usable state word rather than poisoning every draw with NaN.
    let state = (Number.isFinite(seed) ? Math.trunc(seed) : 0) >>> 0;
    return function nextRandom() {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Uniform integer in [minInclusive, maxExclusive). */
export function randomInt(rng, minInclusive, maxExclusive) {
    const span = maxExclusive - minInclusive;
    if (!(span > 0)) {
        throw new RangeError(`randomInt needs maxExclusive > minInclusive, got [${minInclusive}, ${maxExclusive})`);
    }
    // rng() is strictly < 1, so the floor can never reach maxExclusive.
    return minInclusive + Math.floor(rng() * span);
}
/** A uniformly chosen element. Throws on an empty list rather than returning undefined. */
export function pick(rng, items) {
    if (items.length === 0)
        throw new RangeError("pick() called with an empty list");
    const chosen = items[randomInt(rng, 0, items.length)];
    /* c8 ignore next */
    if (chosen === undefined)
        throw new Error("Unreachable: randomInt produced an out-of-range index");
    return chosen;
}
/**
 * One mulberry32 step, as a pure function of its state word.
 *
 * `createRng` hides its state in a closure, which is right for map generation —
 * one generator, used once, never persisted. World-event scheduling is different:
 * it runs across a whole session and lives on `GameState`, which docs/04 wants to
 * stay cleanly serializable so a reconnecting client can be resynced. A closure
 * cannot be serialized; a number can.
 *
 * Same arithmetic as `createRng`, so a given state word produces the same value
 * in both.
 */
export function rngStep(state) {
    const next = (state + 0x6d2b79f5) >>> 0;
    let t = next;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return { value: ((t ^ (t >>> 14)) >>> 0) / 4294967296, next };
}
//# sourceMappingURL=rng.js.map